<!--
  =====================================================================
  PRODUCTS HOME PAGE - PRODUCTS LANDING
  =====================================================================
  
  Landing page for products section displaying all macro-areas and their
  associated product categories as an interactive grid.
  
  FEATURES:
  - Loads products data (macro-areas and categories)
  - Generates submenu from macro-area titles with anchor links
  - Renders macro-areas as expandable sections with product cards
  - Links are dynamically generated with /cassa/ prefix for Nginx routing
  - Fully responsive grid layout with color scheme support
  
  STRUCTURE:
  - PageSubmenu: Navigation between macro-areas
  - Each macro-area has: eyebrow, title, and product cards grid
  - Cards are linked to category detail pages
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />
    
    <PageSubmenu
      v-if="submenuItems.length > 0"
      :mainLabel="state.page.title"
      :sections="submenuItems"
      :overrideExisting="false"
    />

    <main class="prodotti-home">
      <div class="macro-aree">
        <section 
          v-for="area in state.macroaree" 
          :key="area.id"
          :id="area.id"
          class="macro-area"
        >
          <div class="macro-area__header">
            <div v-if="area.id !== 'imprese-professionisti'" class="macro-area__eyebrow">{{ area.eyebrow }}</div>
            <h2 class="macro-area__title">{{ area.title }}</h2>
          </div>
          
          <div class="macro-area__grid">
            <SectionProductCard
              v-for="card in area.cards"
              :key="`${area.id}-${card.slug}`"
              :card="{ ...card, link: `/cassa/prodotti/${area.id}/${card.slug}` }"
            />
          </div>

          <div 
            v-if="area.text"
            class="macro-area__text"
            v-html="sanitizeHtml(area.text)"
          ></div>
        </section>
      </div>
    </main>

    <ArrowUp /> 
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { state, loadPageContent } from '@/store/index.js'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'
import PageSubmenu from '@/components/sections/shared/PageSubmenu.vue'

// ───────────────────────────────────────────────────────────────────
// PRODUCT-SPECIFIC COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionProductCard from '@/components/sections/prodotti/SectionProductCard.vue'

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// SANITIZATION UTILITY
// ───────────────────────────────────────────────────────────────────

function sanitizeHtml(html) {
  return sanitizeHtmlRegex(html || '')
}

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Generates submenu items from macro-areas
 * Each item is an anchor link to the macro-area section
 * 
 * RETURNS:
 * - label: The macro-area title
 * - target: The anchor ID (#macro-area-id)
 * 
 * @returns {Array} Array of submenu items
 */
const submenuItems = computed(() => {
  if (!Array.isArray(state.macroaree)) return []
  return state.macroaree.map(a => ({
    label: a.title,
    target: `#${a.id}`
  }))
})

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Load products data on component mount
 * Only loads if macro-areas haven't been loaded yet
 */
onMounted(async () => {
  if (!state.macroaree || state.macroaree.length === 0) {
    await loadPageContent('prodotti')
  }
})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

/* ------------------------------------------------------------------
   Page container and macro-areas layout
------------------------------------------------------------------ */
.prodotti-home {
  padding: 0;
  background: $clr-bg-page;

  .macro-aree {
    display: flex;
    flex-direction: column;
    @include flex-gap($space-3, column);
    padding: $space-4 0;
    max-width: $container-max-width;
    margin: 0 auto;
  }

  .macro-area {
    padding: 0 $container-padding-x;
    scroll-margin-top: calc(var(--header-total-height) + var(--page-submenu-height) + 2rem);

    &__header {
      margin-bottom: $space-3;
      text-align: left;
    }

    /* Eyebrow: green institutional with classic offset bar */
    &__eyebrow {
      position: relative;
      display: inline-block;
      color: $clr-tertiary;
      font-size: $font-eyebrow;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin: 0 0 $space-1 60px;
      padding-left: $space-2;
      cursor: default;
      white-space: nowrap;
      transition: all 0.3s ease;

      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: -50px;
        width: 40%;
        height: 12px;
        background-color: rgba($clr-tertiary, 0.7);
        transition: all 0.5s ease;
      }

      @media (hover: hover) {
        &:hover {
          color: $clr-tertiary;

          &::before {
            background: rgba($clr-tertiary, 0.8);
            width: 120%;
            transform: scaleY(1.2);
          }
        }
      }
    }

    /* Macro-area title */
    &__title {
      font-family: 'Karla', sans-serif;
      font-size: $font-title;
      font-weight: 300;
      color: $text-color;
      margin: 0 0 0 60px;
    }

    /* Macro-area HTML text (optional) */
    &__text {
      font-size: $font-base;
      line-height: $line-height-base;
      color: $clr-brand-gray;
      margin-top: $space-5;
      margin-bottom: 0;

      :deep(p) { margin-bottom: $paragraph-spacing; }
      :deep(ul), :deep(ol) { margin: $list-spacing 0; padding-left: $list-indent; }
      :deep(li) { margin-bottom: $space-0-75; }
      :deep(strong) { font-weight: 700; color: $clr-brand-gray; }
      :deep(em) { font-style: italic; }
      :deep(a) {
        color: $clr-tertiary;
      }
    }

    /* Product cards grid with responsive columns */
    &__grid {
      display: grid;
      gap: $space-2;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      
      @media (min-width: 1200px) {
        grid-template-columns: repeat(4, 1fr);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Medium displays (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .prodotti-home {
    .macro-aree {
      @include flex-gap($space-5, column);
      padding: $space-3-5 0;
    }

    .macro-area {
      &__header {
        margin-bottom: $space-2-5;
      }

      &__eyebrow {
        margin-left: 50px;
        &::before { left: -40px; width: 40%; }
      }

      &__title {
        font-size: $font-title-lg;
        margin-left: 50px;
      }

      &__text {
        font-size: $font-base-lg;
        margin-top: $space-5;
      }

      &__grid {
        gap: $space-1-75;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      }
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Tablet (≤920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .prodotti-home {
    .macro-aree {
      @include flex-gap($space-4, column);
      padding: $space-3 0;
    }

    .macro-area {
      padding: 0 $container-padding-x-mob;

      &__header {
        margin-bottom: $space-2;
      }

      &__eyebrow {
        font-size: $font-eyebrow-sm;
        margin-bottom: $space-0-25;
        margin-left: 30px;
        &::before { left: -30px; width: 40%; }
      }

      &__title {
        font-size: $font-title-sm;
        margin-left: 30px;
      }

      &__text {
        font-size: $font-base-sm;
        margin-top: $space-4;
      }

      &__grid {
        gap: $space-1-5;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      }
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .prodotti-home {
    .macro-aree {
      @include flex-gap($space-3, column);
      padding: $space-2-5 0;
    }

    .macro-area {
      &__header {
        margin-bottom: $space-1-5;
      }

      &__eyebrow {
        font-size: $font-eyebrow-xs;
        margin-left: 20px;
        &::before { left: -20px; width: 40%; }
      }

      &__title {
        font-size: $font-title-xs;
        margin-left: 20px;
      }

      &__text {
        font-size: $font-base-xs;
        margin-top: $space-3-5;
      }

      &__grid {
        gap: $space-1-25;
        grid-template-columns: 1fr;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Very small mobile (≤360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .prodotti-home {
    .macro-aree {
      @include flex-gap($space-2-5, column);
      padding: $space-2 0;
    }

    .macro-area {
      &__header {
        margin-bottom: $space-1;
      }

      &__eyebrow {
        font-size: $font-eyebrow-xxs;
        margin-left: 15px;
        &::before { left: -15px; width: 40%; }
      }

      &__title {
        font-size: $font-title-xxs;
        margin-left: 15px;
      }

      &__text {
        font-size: $font-base-xxs;
        margin-top: $space-3;
      }

      &__grid {
        gap: $space-1;
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   L'eyebrow aveva un padding-left che spostava il TESTO a destra
   lasciando il box dov'era: era quello a disallinearlo dal titolo.
   Qui viene azzerato, cosi' l'eyebrow parte esattamente dove parte
   il titolo. Nessun titolo viene spostato.
   Il ::before e' position:absolute e non dipende dal padding.
   Classe ripetuta tre volte per battere le regole annidate originali.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .macro-area__eyebrow.macro-area__eyebrow.macro-area__eyebrow {
    padding-left: 0;
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO AL PARAGRAFO
   eyebrow, titolo e corpo del testo devono partire dalla stessa x,
   quella di "chi siamo": 44px a 390, 39 a 360, 54 a 600, 102 a 768-920,
   122 a 1024. Valori ricavati misurando il primo glifo di ogni elemento.
   Classe ripetuta tre volte per battere le regole annidate originali;
   blocchi in fondo al file perche' l'ordine decide fra breakpoint.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .macro-area__eyebrow.macro-area__eyebrow.macro-area__eyebrow {
    margin-left: 42px;
    padding-left: 0;
  }

  .macro-area__title.macro-area__title.macro-area__title {
    margin-left: 42px;
  }

  .macro-area__text.macro-area__text.macro-area__text {
    margin-left: 42px;
  }
}

@media (max-width: 920px) {
  .macro-area__eyebrow.macro-area__eyebrow.macro-area__eyebrow {
    margin-left: 30px;
    padding-left: 0;
  }

  .macro-area__title.macro-area__title.macro-area__title {
    margin-left: 30px;
  }

  .macro-area__text.macro-area__text.macro-area__text {
    margin-left: 30px;
  }
}

@media (max-width: 480px) {
  .macro-area__eyebrow.macro-area__eyebrow.macro-area__eyebrow {
    margin-left: 20px;
    padding-left: 0;
  }

  .macro-area__title.macro-area__title.macro-area__title {
    margin-left: 20px;
  }

  .macro-area__text.macro-area__text.macro-area__text {
    margin-left: 20px;
  }
}

@media (max-width: 360px) {
  .macro-area__eyebrow.macro-area__eyebrow.macro-area__eyebrow {
    margin-left: 15px;
    padding-left: 0;
  }

  .macro-area__title.macro-area__title.macro-area__title {
    margin-left: 15px;
  }

  .macro-area__text.macro-area__text.macro-area__text {
    margin-left: 15px;
  }
}
</style>
